import { isPuzzleMoveCorrect } from '@/core/helpers/puzzle.helper';
import { GamChessboardOrientation } from '@/views/composables/constants/components/gamChessboard.constants';
import { Chess, type Square } from 'chess.js';
import { computed, ref, watch } from 'vue';

export type TracePhase = 'play_cta' | 'wrong' | 'correct' | 'interactive';

export interface PuzzleData {
  id: number;
  fen: string;
  setupMove: string;
  solutionMove: string;
  rating: number;
  hasSolved?: boolean;
  animateReplay?: boolean;
  replayMove?: string;
  pendingPlayCta?: boolean;
  skipSetupAnimation?: boolean;
}

export function usePuzzleEngine(puzzleData: () => PuzzleData | null) {
  const boardFen = ref<string>('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
  const readyFen = ref<string>('');
  const isReady = ref(false);
  const isSolved = ref(false);
  const isWrong = ref(false);
  const isLocked = ref(false);
  const pendingReplay = ref(false);
  const replayWasCorrect = ref(false);
  const playerColor = ref<'w' | 'b'>('w');
  const lastMoveFrom = ref<string | undefined>();
  const lastMoveTo = ref<string | undefined>();

  let storedReplayChess: Chess | null = null;
  let storedReplayFrom: Square | null = null;
  let storedReplayTo: Square | null = null;
  let storedReplayPromo: string | undefined;
  let storedSolutionMove = '';
  let storedFen = '';
  let storedSetupMove = '';

  const canInteract = computed(() => isReady.value && !isSolved.value && !isWrong.value && !isLocked.value);

  function initPuzzle(data: PuzzleData) {
    isReady.value = false;
    isSolved.value = !!data.hasSolved;
    isWrong.value = false;
    isLocked.value = false;
    pendingReplay.value = false;
    replayWasCorrect.value = false;
    storedReplayChess = null;

    const chess = new Chess(data.fen);
    playerColor.value = chess.turn() === 'w' ? 'b' : 'w';

    const setupFrom = data.setupMove.substring(0, 2) as Square;
    const setupTo = data.setupMove.substring(2, 4) as Square;
    const setupPromo = data.setupMove.length > 4 ? data.setupMove[4] : undefined;

    if (data.hasSolved) {
      const replayMoveUci = data.replayMove || data.solutionMove;
      const solFrom = replayMoveUci.substring(0, 2) as Square;
      const solTo = replayMoveUci.substring(2, 4) as Square;
      const solPromo = replayMoveUci.length > 4 ? replayMoveUci[4] : undefined;

      if (data.pendingPlayCta) {
        isSolved.value = false;
        chess.move({ from: setupFrom, to: setupTo, promotion: setupPromo });
        boardFen.value = chess.fen();
        lastMoveFrom.value = setupFrom;
        lastMoveTo.value = setupTo;
        readyFen.value = boardFen.value;
        isReady.value = true;
        pendingReplay.value = true;

        storedReplayChess = chess;
        storedReplayFrom = solFrom;
        storedReplayTo = solTo;
        storedReplayPromo = solPromo;
        storedSolutionMove = data.solutionMove;
        storedFen = data.fen;
        storedSetupMove = data.setupMove;
        return;
      }

      if (data.animateReplay) {
        isSolved.value = false;
        boardFen.value = data.fen;

        setTimeout(() => {
          chess.move({ from: setupFrom, to: setupTo, promotion: setupPromo });
          boardFen.value = chess.fen();
          lastMoveFrom.value = setupFrom;
          lastMoveTo.value = setupTo;

          setTimeout(() => {
            const moved = chess.move({ from: solFrom, to: solTo, promotion: solPromo });
            if (moved) {
              boardFen.value = chess.fen();
              lastMoveFrom.value = solFrom;
              lastMoveTo.value = solTo;
            }
            readyFen.value = boardFen.value;
            isSolved.value = true;
            isReady.value = true;
          }, 2000);
        }, 600);
      } else {
        chess.move({ from: setupFrom, to: setupTo, promotion: setupPromo });
        const moved = chess.move({ from: solFrom, to: solTo, promotion: solPromo });
        if (moved) {
          lastMoveFrom.value = solFrom;
          lastMoveTo.value = solTo;
        }
        boardFen.value = chess.fen();
        readyFen.value = boardFen.value;
        isReady.value = true;
      }
      return;
    }

    boardFen.value = data.fen;

    if (data.skipSetupAnimation) {
      chess.move({ from: setupFrom, to: setupTo, promotion: setupPromo });
      readyFen.value = chess.fen();
      boardFen.value = readyFen.value;
      lastMoveFrom.value = setupFrom;
      lastMoveTo.value = setupTo;
      isReady.value = true;
    } else {
      setTimeout(() => {
        chess.move({ from: setupFrom, to: setupTo, promotion: setupPromo });
        readyFen.value = chess.fen();
        boardFen.value = readyFen.value;
        lastMoveFrom.value = setupFrom;
        lastMoveTo.value = setupTo;
        isReady.value = true;
      }, 600);
    }
  }

  function playReplayMove() {
    if (!storedReplayChess || !storedReplayFrom || !storedReplayTo) return;

    const moved = storedReplayChess.move({
      from: storedReplayFrom,
      to: storedReplayTo,
      promotion: storedReplayPromo,
    });
    if (moved) {
      boardFen.value = storedReplayChess.fen();
      lastMoveFrom.value = storedReplayFrom;
      lastMoveTo.value = storedReplayTo;
    }

    const replayUci = `${storedReplayFrom}${storedReplayTo}${storedReplayPromo || ''}`;
    replayWasCorrect.value = isPuzzleMoveCorrect(storedFen, storedSetupMove, storedSolutionMove, replayUci);
    readyFen.value = boardFen.value;
    isSolved.value = true;
    pendingReplay.value = false;
    storedReplayChess = null;
  }

  function checkMove(from: string, to: string, promotion?: string): boolean {
    if (!canInteract.value) return false;

    const data = puzzleData();
    if (!data) return false;

    const attemptUci = `${from}${to}${promotion || ''}`;

    if (isPuzzleMoveCorrect(data.fen, data.setupMove, data.solutionMove, attemptUci)) {
      isSolved.value = true;
      isWrong.value = false;
      lastMoveFrom.value = from;
      lastMoveTo.value = to;
      return true;
    }

    isWrong.value = true;
    isLocked.value = true;

    return false;
  }

  const orientation = computed(() =>
    playerColor.value === 'w' ? GamChessboardOrientation.WHITE : GamChessboardOrientation.BLACK,
  );

  watch(
    puzzleData,
    (data) => {
      if (data) initPuzzle(data);
    },
    { immediate: true },
  );

  return {
    boardFen,
    isReady,
    isSolved,
    isWrong,
    isLocked,
    canInteract,
    playerColor,
    orientation,
    lastMoveFrom,
    lastMoveTo,
    pendingReplay,
    replayWasCorrect,
    checkMove,
    playReplayMove,
  };
}
